import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Loader2, Edit, Save } from "lucide-react";
import { toast } from "sonner";
import { Shop } from "@/lib/supabase/shops/types";
import { updateShop } from "@/lib/supabase/shops";
import { useMetaPixel } from "./hooks/useMetaPixel";
import { MetaPixelSection } from "./components/MetaPixelSection";
import { GoogleAnalyticsSection } from "./components/GoogleAnalyticsSection";
import { GoogleAdsSection } from "./components/GoogleAdsSection";
import { SnapchatAdsSection } from "./components/SnapchatAdsSection";
import { LinkedinAdsSection } from "./components/LinkedinAdsSection";
import { TiktokAdsSection } from "./components/TiktokAdsSection";

interface TrackingSettingsSectionProps {
  shop: Shop;
}

export const TrackingSettingsSection: React.FC<TrackingSettingsSectionProps> = ({ shop }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  const [metaPixelId, setMetaPixelId] = useState(
    shop.tracking_settings?.meta?.pixel_id || shop.meta_pixel_id || ""
  );
  const [metaAccessToken, setMetaAccessToken] = useState(
    shop.tracking_settings?.meta?.access_token || shop.meta_access_token || ""
  );
  
  const [googleAnalyticsId, setGoogleAnalyticsId] = useState(
    shop.tracking_settings?.google_analytics?.id || shop.google_analytics_id || ""
  );
  const [googleAnalyticsApiSecret, setGoogleAnalyticsApiSecret] = useState(
    shop.tracking_settings?.google_analytics?.api_secret || shop.google_analytics_api_secret || ""
  );
  
  const [googleAdsId, setGoogleAdsId] = useState(
    shop.tracking_settings?.google_ads?.id || shop.google_ads_id || ""
  );
  const [googleAdsConversionId, setGoogleAdsConversionId] = useState(
    shop.tracking_settings?.google_ads?.conversion_id || shop.google_ads_conversion_id || ""
  );
  const [googleAdsApiKey, setGoogleAdsApiKey] = useState(
    shop.tracking_settings?.google_ads?.api_key || shop.google_ads_api_key || ""
  );
  
  const [snapchatPixelId, setSnapchatPixelId] = useState(
    shop.tracking_settings?.snapchat?.pixel_id || shop.snapchat_pixel_id || ""
  );
  const [snapchatApiToken, setSnapchatApiToken] = useState(
    shop.tracking_settings?.snapchat?.api_token || shop.snapchat_api_token || ""
  );
  
  const [linkedinPixelId, setLinkedinPixelId] = useState(
    shop.tracking_settings?.linkedin?.pixel_id || shop.linkedin_pixel_id || ""
  );
  const [linkedinApiToken, setLinkedinApiToken] = useState(
    shop.tracking_settings?.linkedin?.api_token || shop.linkedin_api_token || ""
  );
  
  const [tiktokPixelId, setTiktokPixelId] = useState(
    shop.tracking_settings?.tiktok?.pixel_id || shop.tiktok_pixel_id || ""
  );
  const [tiktokApiToken, setTiktokApiToken] = useState(
    shop.tracking_settings?.tiktok?.api_token || shop.tiktok_api_token || ""
  );

  const [metaPixelEnabled, setMetaPixelEnabled] = useState(
    shop.tracking_settings?.meta?.enabled !== undefined 
      ? shop.tracking_settings.meta.enabled 
      : Boolean(metaPixelId)
  );
  const [metaApiEnabled, setMetaApiEnabled] = useState(
    shop.tracking_settings?.meta?.api_enabled !== undefined 
      ? shop.tracking_settings.meta.api_enabled 
      : Boolean(metaAccessToken)
  );
  
  const [googleAnalyticsEnabled, setGoogleAnalyticsEnabled] = useState(
    shop.tracking_settings?.google_analytics?.enabled !== undefined 
      ? shop.tracking_settings.google_analytics.enabled 
      : Boolean(googleAnalyticsId)
  );
  const [googleAnalyticsApiEnabled, setGoogleAnalyticsApiEnabled] = useState(
    shop.tracking_settings?.google_analytics?.api_enabled !== undefined 
      ? shop.tracking_settings.google_analytics.api_enabled 
      : Boolean(googleAnalyticsApiSecret)
  );
  
  const [googleAdsEnabled, setGoogleAdsEnabled] = useState(
    shop.tracking_settings?.google_ads?.enabled !== undefined 
      ? shop.tracking_settings.google_ads.enabled 
      : Boolean(googleAdsId)
  );
  const [googleAdsApiEnabled, setGoogleAdsApiEnabled] = useState(
    shop.tracking_settings?.google_ads?.api_enabled !== undefined 
      ? shop.tracking_settings.google_ads.api_enabled 
      : Boolean(googleAdsApiKey)
  );
  
  const [snapchatPixelEnabled, setSnapchatPixelEnabled] = useState(
    shop.tracking_settings?.snapchat?.enabled !== undefined 
      ? shop.tracking_settings.snapchat.enabled 
      : Boolean(snapchatPixelId)
  );
  const [snapchatApiEnabled, setSnapchatApiEnabled] = useState(
    shop.tracking_settings?.snapchat?.api_enabled !== undefined 
      ? shop.tracking_settings.snapchat.api_enabled 
      : Boolean(snapchatApiToken)
  );
  
  const [linkedinPixelEnabled, setLinkedinPixelEnabled] = useState(
    shop.tracking_settings?.linkedin?.enabled !== undefined 
      ? shop.tracking_settings.linkedin.enabled 
      : Boolean(linkedinPixelId)
  );
  const [linkedinApiEnabled, setLinkedinApiEnabled] = useState(
    shop.tracking_settings?.linkedin?.api_enabled !== undefined 
      ? shop.tracking_settings.linkedin.api_enabled 
      : Boolean(linkedinApiToken)
  );
  
  const [tiktokPixelEnabled, setTiktokPixelEnabled] = useState(
    shop.tracking_settings?.tiktok?.enabled !== undefined 
      ? shop.tracking_settings.tiktok.enabled 
      : Boolean(tiktokPixelId)
  );
  const [tiktokApiEnabled, setTiktokApiEnabled] = useState(
    shop.tracking_settings?.tiktok?.api_enabled !== undefined 
      ? shop.tracking_settings.tiktok.api_enabled 
      : Boolean(tiktokApiToken)
  );

  const { sendEvent } = useMetaPixel(metaPixelEnabled ? metaPixelId : "");

  const handleSave = async () => {
    try {
      setIsSaving(true);
      
      const trackingSettings = {
        meta: {
          pixel_id: metaPixelId || null,
          access_token: metaAccessToken || null,
          enabled: metaPixelEnabled,
          api_enabled: metaApiEnabled
        },
        google_analytics: {
          id: googleAnalyticsId || null,
          api_secret: googleAnalyticsApiSecret || null,
          enabled: googleAnalyticsEnabled,
          api_enabled: googleAnalyticsApiEnabled
        },
        google_ads: {
          id: googleAdsId || null,
          conversion_id: googleAdsConversionId || null,
          api_key: googleAdsApiKey || null,
          enabled: googleAdsEnabled,
          api_enabled: googleAdsApiEnabled
        },
        snapchat: {
          pixel_id: snapchatPixelId || null,
          api_token: snapchatApiToken || null,
          enabled: snapchatPixelEnabled,
          api_enabled: snapchatApiEnabled
        },
        linkedin: {
          pixel_id: linkedinPixelId || null,
          api_token: linkedinApiToken || null,
          enabled: linkedinPixelEnabled,
          api_enabled: linkedinApiEnabled
        },
        tiktok: {
          pixel_id: tiktokPixelId || null,
          api_token: tiktokApiToken || null,
          enabled: tiktokPixelEnabled,
          api_enabled: tiktokApiEnabled
        }
      };
      
      const shopData = {
        tracking_settings: trackingSettings,
        meta_pixel_id: metaPixelId,
        meta_access_token: metaAccessToken,
        google_analytics_id: googleAnalyticsId,
        google_analytics_api_secret: googleAnalyticsApiSecret,
        google_ads_id: googleAdsId,
        google_ads_conversion_id: googleAdsConversionId,
        google_ads_api_key: googleAdsApiKey,
        snapchat_pixel_id: snapchatPixelId,
        snapchat_api_token: snapchatApiToken,
        linkedin_pixel_id: linkedinPixelId,
        linkedin_api_token: linkedinApiToken,
        tiktok_pixel_id: tiktokPixelId,
        tiktok_api_token: tiktokApiToken
      };
      
      const { success, error } = await updateShop(shop.id, shopData);
      
      if (success) {
        toast.success("Paramètres de tracking enregistrés avec succès");
        setIsEditing(false);

        if (metaPixelId && metaPixelEnabled && metaPixelId !== shop.meta_pixel_id) {
          sendEvent('PixelConfigured', {
            pixel_id: metaPixelId,
            shop_id: shop.id,
            shop_name: shop.name,
            event_time: Date.now()
          });
        }
      } else {
        throw new Error(error?.message || "Une erreur est survenue lors de la mise à jour des paramètres de tracking");
      }
    } catch (error: any) {
      toast.error("Erreur", {
        description: error.message || "Une erreur est survenue lors de la mise à jour des paramètres de tracking"
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="md:col-span-2">
      <Card className="shadow-md border-0 dark:border dark:border-gray-800">
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <div>
            <h2 className="text-xl font-bold">Paramètres de Tracking</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Configurez les identifiants pour suivre les conversions sur différentes plateformes publicitaires
            </p>
          </div>
          
          <div>
            {isEditing ? (
              <Button 
                onClick={handleSave} 
                disabled={isSaving}
                className="flex items-center gap-2"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Enregistrement...</span>
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    <span>Enregistrer</span>
                  </>
                )}
              </Button>
            ) : (
              <Button 
                variant="outline" 
                onClick={() => setIsEditing(true)}
                className="flex items-center gap-2"
              >
                <Edit className="h-4 w-4" />
                <span>Modifier</span>
              </Button>
            )}
          </div>
        </CardHeader>
        
        <CardContent>
          <div className="space-y-6">
            <MetaPixelSection 
              metaPixelId={metaPixelId}
              setMetaPixelId={setMetaPixelId}
              metaAccessToken={metaAccessToken}
              setMetaAccessToken={setMetaAccessToken}
              isEditing={isEditing}
              isPixelEnabled={metaPixelEnabled}
              setIsPixelEnabled={setMetaPixelEnabled}
              isApiEnabled={metaApiEnabled}
              setIsApiEnabled={setMetaApiEnabled}
            />
            
            <Separator />
            
            <GoogleAnalyticsSection 
              googleAnalyticsId={googleAnalyticsId}
              setGoogleAnalyticsId={setGoogleAnalyticsId}
              googleAnalyticsApiSecret={googleAnalyticsApiSecret}
              setGoogleAnalyticsApiSecret={setGoogleAnalyticsApiSecret}
              isEditing={isEditing}
              isEnabled={googleAnalyticsEnabled}
              setIsEnabled={setGoogleAnalyticsEnabled}
              isApiEnabled={googleAnalyticsApiEnabled}
              setIsApiEnabled={setGoogleAnalyticsApiEnabled}
            />
            
            <Separator />
            
            <GoogleAdsSection 
              googleAdsId={googleAdsId}
              setGoogleAdsId={setGoogleAdsId}
              googleAdsConversionId={googleAdsConversionId}
              setGoogleAdsConversionId={setGoogleAdsConversionId}
              googleAdsApiKey={googleAdsApiKey}
              setGoogleAdsApiKey={setGoogleAdsApiKey}
              isEditing={isEditing}
              isEnabled={googleAdsEnabled}
              setIsEnabled={setGoogleAdsEnabled}
              isApiEnabled={googleAdsApiEnabled}
              setIsApiEnabled={setGoogleAdsApiEnabled}
            />
            
            <Separator />
            
            <SnapchatAdsSection 
              snapchatPixelId={snapchatPixelId}
              setSnapchatPixelId={setSnapchatPixelId}
              snapchatApiToken={snapchatApiToken}
              setSnapchatApiToken={setSnapchatApiToken}
              isEditing={isEditing}
              isPixelEnabled={snapchatPixelEnabled}
              setIsPixelEnabled={setSnapchatPixelEnabled}
              isApiEnabled={snapchatApiEnabled}
              setIsApiEnabled={setSnapchatApiEnabled}
            />
            
            <Separator />
            
            <LinkedinAdsSection 
              linkedinPixelId={linkedinPixelId}
              setLinkedinPixelId={setLinkedinPixelId}
              linkedinApiToken={linkedinApiToken}
              setLinkedinApiToken={setLinkedinApiToken}
              isEditing={isEditing}
              isPixelEnabled={linkedinPixelEnabled}
              setIsPixelEnabled={setLinkedinPixelEnabled}
              isApiEnabled={linkedinApiEnabled}
              setIsApiEnabled={setLinkedinApiEnabled}
            />
            
            <Separator />
            
            <TiktokAdsSection 
              tiktokPixelId={tiktokPixelId}
              setTiktokPixelId={setTiktokPixelId}
              tiktokApiToken={tiktokApiToken}
              setTiktokApiToken={setTiktokApiToken}
              isEditing={isEditing}
              isPixelEnabled={tiktokPixelEnabled}
              setIsPixelEnabled={setTiktokPixelEnabled}
              isApiEnabled={tiktokApiEnabled}
              setIsApiEnabled={setTiktokApiEnabled}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TrackingSettingsSection;
